import { defaultFlows } from '@/configs/default-flows'
import { wizardConfig } from '@/configs/wizard-element-config'
import type { Flow } from '@/types/flow-config-type'
import { axiosClient } from '@/utils/axios-client'
import { DEVICE_ID, USER_ID } from '@/utils/constants'
import {
  FlowTypes,
  require,
  useToastStore,
  type FlowConfig,
  type FlowField,
  type FlowPage,
  type HiddenField,
} from '@logistiek-zonder-papier/lzp-library'
import { isAxiosError, type AxiosResponse } from 'axios'
import type { UUID } from 'node:crypto'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const FLOW_COLOUR_KEYS = [
  'buttonColour',
  'textColour',
  'activityIndicatorColour',
  'activityIndicatorTextColour',
] as const
type FlowColourKey = (typeof FLOW_COLOUR_KEYS)[number]

export const useFlowConfigStore = defineStore('flowConfigStore', () => {
  const currentPage = ref(0)
  const config = ref<FlowConfig | null>(null)
  const toastStore = useToastStore()
  const mimicDeviceId = ref(localStorage.getItem(DEVICE_ID) ?? null)
  const mimicUserId = ref(localStorage.getItem(USER_ID) ?? null)
  const flows = ref<FlowConfig[]>([])
  const savedFlowColours = ref<Record<string, Pick<FlowConfig, FlowColourKey>>>({})

  async function getFlowConfig(subAppId: string, type: string): Promise<void> {
    const configResponse = await axiosClient.request<FlowConfig>({
      url: `/sub-app/${subAppId}/flow-configuration/by-type/${type}`,
      method: 'GET',
    })

    if (!configResponse) return

    const configId = configResponse.data.id

    const pagesResponse = await axiosClient.request<FlowConfig>({
      url: `/sub-app/${subAppId}/flow-configuration/total/${configId}`,
      method: 'GET',
    })

    if (!pagesResponse) return
    config.value = { ...pagesResponse.data }
  }

  async function fetchFlows(subAppId: UUID) {
    const response = await axiosClient.request<FlowConfig[]>({
      url: `/sub-app/${subAppId}/flow-configuration`,
      method: 'GET',
    })

    const registration = response.data.find(flow => flow.type === FlowTypes.registration)

    if (registration && registration.order !== 0) {
      const others = response.data
        .filter(flow => flow.id !== registration.id)
        .sort((one, two) => one.order - two.order)

      const correctedOrder = [
        { ...registration, order: 0 },
        ...others.map((flow, index) => ({ ...flow, order: index + 1 })),
      ]

      await Promise.all(correctedOrder.map(flow => updateFlow(flow)))

      flows.value = correctedOrder
    } else {
      flows.value = response.data
    }

    snapshotFlowColours()
    return flows.value
  }

  async function deleteFlow(config: FlowConfig) {
    await Promise.all(config.pages.map(deleteFlowPageFields))
    await Promise.all(config.pages.map(page => deleteFlowPage(page.id)))

    await axiosClient.request({
      url: `/sub-app/${config.subAppId}/flow-configuration/${config.id}`,
      method: 'DELETE',
    })
  }

  async function getTotalFlow(config: FlowConfig) {
    return await axiosClient.request({
      url: `/sub-app/${config.subAppId}/flow-configuration/total/${config.id}`,
      method: 'GET',
    })
  }

  async function copyFlowToSubApp(config: FlowConfig, subAppId: UUID) {
    return await axiosClient.request({
      url: `sub-app/${config.subAppId}/flow-configuration/${config.id}/copy-flow`,
      method: 'POST',
      data: {
        subAppId,
      },
    })
  }

  async function saveFlow(
    type: string,
    subAppId: UUID,
    label: string,
    description: string
  ): Promise<AxiosResponse | void> {
    const data: Partial<FlowConfig> = {
      type: type,
      subAppId: subAppId,
      hidden: type === FlowTypes.registration,
      label: label,
      description: description,
      requiresRegistration: true,
    }

    const defaultFlow = defaultFlows.find(flow => flow.type === type)

    if (defaultFlow != null) {
      data.label ??= defaultFlow.label
      data.description ??= defaultFlow.description
    }

    return await axiosClient.request({
      url: `/sub-app/${subAppId}/flow-configuration`,
      method: 'POST',
      data: data,
    })
  }

  async function updateFlow(config: FlowConfig) {
    const data: Partial<FlowConfig> = {
      subAppId: config.subAppId,
      label: config.label,
      description: config.description,
      buttonColour: config.buttonColour,
      textColour: config.textColour,
      hidden: config.hidden,
      order: config.order,
      requiresRegistration: config.requiresRegistration,
      predicateCombinator: config.predicateCombinator,
      predicates: config.predicates,
      activityIndicatorPath: config.activityIndicatorPath,
      activityIndicatorColour: config.activityIndicatorColour,
      activityIndicatorTextColour: config.activityIndicatorTextColour,
    }

    await axiosClient.request({
      url: `/sub-app/${config.subAppId}/flow-configuration/${config.id}`,
      method: 'PATCH',
      data,
    })
  }

  async function createFlow(type: string, subAppId: UUID, label: string, description: string) {
    const response = await saveFlow(type, subAppId, label, description)

    if (!response) throw Error('Something went wrong with creating the flow')

    await saveFlowPage(subAppId, response.data?.id, type, 0)
    await getFlowConfig(subAppId, type)
  }

  async function importFlow(flow: string, subAppId: UUID) {
    return await axiosClient.request({
      url: `/sub-app/${subAppId}/flow-configuration/import-flow`,
      method: 'POST',
      data: JSON.parse(flow),
    })
  }

  async function saveField(selectedField: FlowField, order: number): Promise<AxiosResponse | void> {
    if (!config.value) return
    return await axiosClient.request({
      url: `/sub-app/${config.value.subAppId}/flow-configuration/${config.value.id}/flow-page/${config.value.pages[currentPage.value]?.id}/flow-field`,
      method: 'POST',
      data: {
        type: selectedField?.type,
        field: selectedField,
        order,
      },
    })
  }

  async function updateField(selectedField: FlowField): Promise<AxiosResponse | void> {
    if (!config.value) return
    return await axiosClient.request({
      url: `/sub-app/${config.value.subAppId}/flow-configuration/${config.value.id}/flow-page/${config.value.pages[currentPage.value]?.id}/flow-field/${selectedField?.id}`,
      method: 'PATCH',
      data: {
        field: {
          ...selectedField,
        },
      },
    })
  }

  async function syncAllFieldOrders(startIndex: number): Promise<void> {
    if (!config.value) return

    const page = config.value.pages[currentPage.value]
    if (!page) return

    for (let index = startIndex; index < page.fields.length; index++) {
      const field = page.fields[index]

      if (!field) return

      await axiosClient.request({
        url: `/sub-app/${config.value.subAppId}/flow-configuration/${config.value.id}/flow-page/${config.value.pages[currentPage.value]?.id}/flow-field/${field.id}`,
        method: 'PATCH',
        data: {
          order: index,
        },
      })
    }
  }

  async function syncPageOrder(): Promise<void> {
    if (!config.value) return

    for (const [index, page] of config.value.pages.entries()) {
      await axiosClient.request({
        url: `/sub-app/${config.value.subAppId}/flow-configuration/${config.value.id}/flow-page/${page.id}`,
        method: 'PATCH',
        data: {
          order: index,
        },
      })
    }
  }

  async function deleteField(id: string, pageId: string): Promise<void> {
    if (!config.value) return

    await axiosClient.request({
      url: `/sub-app/${config.value.subAppId}/flow-configuration/${config.value.id}/flow-page/${pageId}/flow-field/${id}`,
      method: 'DELETE',
    })
  }

  async function saveFlowPage(
    subAppId: string,
    flowConfigId: string,
    type: string,
    order: number,
    showSubmit: boolean = true,
    showName: boolean = true
  ): Promise<void> {
    await axiosClient.request({
      url: `/sub-app/${subAppId}/flow-configuration/${flowConfigId}/flow-page`,
      method: 'POST',
      data: {
        name: `${type} flow page`,
        order,
        showSubmit,
        showName,
      },
    })
  }

  async function updateFlowPage(pageId: string): Promise<void> {
    if (!config.value) return

    const page = config.value.pages.find(page => page.id === pageId)
    if (!page) return

    await axiosClient.request({
      url: `/sub-app/${config.value.subAppId}/flow-configuration/${config.value.id}/flow-page/${pageId}`,
      method: 'PATCH',
      data: page,
    })
  }

  async function deleteFlowPage(pageId: string): Promise<void> {
    if (!config.value) return

    const page = config.value.pages.find(page => page.id === pageId)
    if (!page) return

    await axiosClient.request({
      url: `/sub-app/${config.value.subAppId}/flow-configuration/${config.value.id}/flow-page/${pageId}`,
      method: 'DELETE',
    })
  }

  async function deleteFlowPageFields(page: FlowPage) {
    await Promise.all(
      page.fields.map(field =>
        deleteField(require(field.id, 'Cannot delete field without id'), page.id)
      )
    )
  }

  async function createFlowField(
    type: string,
    selectedFieldId: string | undefined
  ): Promise<string | void> {
    if (!config.value) return

    const page = config.value.pages[currentPage.value]
    if (!page?.fields) return

    const elementConfig = wizardConfig.find(({ type: wizardType }) => wizardType === type)

    const newField = require(elementConfig, `WizardConfig does not have type: ${type}`).createField(
      page.dto
    )
    const fields = page.fields

    const selectedIndex = fields.findIndex(
      (formElement: FlowField) => formElement?.id === selectedFieldId
    )

    const insertIndex = selectedIndex !== -1 ? selectedIndex + 1 : fields.length

    const response = await saveField(newField, insertIndex)

    if (response?.data) {
      fields.splice(insertIndex, 0, response.data)
    }

    await syncAllFieldOrders(insertIndex + 1)
    return response?.data.id
  }

  async function selectFlow(subAppId: UUID, flow: Flow): Promise<void> {
    try {
      await getFlowConfig(subAppId, flow.type)
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        await createDefaultFlow(subAppId, flow)
        await getFlowConfig(subAppId, flow.type)
        if (flow.type === FlowTypes.registration) {
          await applyRegistrationPageDefaults()
        }
      } else {
        toastStore.error(`${error}: invalid sub-app id`)
        throw error
      }
    }
  }

  async function createDefaultFlow(subAppId: UUID, flow: Flow): Promise<void> {
    const defaultFlow = require(defaultFlows.find(
      defaultFlow => defaultFlow.type === flow.type
    ), 'Trying to create default flow without config')

    const response = await saveFlow(flow.type, subAppId, defaultFlow.label, defaultFlow.description)

    if (!response) return

    await saveFlowPage(subAppId, response.data?.id, flow.type, 0)
  }

  async function applyRegistrationPageDefaults(): Promise<void> {
    const page = config.value?.pages[0]
    if (!page) return

    page.endpoint = '/sub-app-registrations'
    page.dto = 'payload'
    const hiddenFields = mergeHiddenFields(page.hiddenFields, [
      { key: 'payload.appUserId', value: '${userId}' },
      { key: 'payload.subAppId', value: '${subAppId}' },
    ])

    for (const hiddenField of hiddenFields) {
      await createHiddenField({ key: hiddenField.key, value: hiddenField.value }, page.id)
    }

    await updateFlowPage(page.id)
  }

  function mergeHiddenFields(existing: HiddenField[], incoming: HiddenField[]): HiddenField[] {
    const existingKeys = new Set(existing.map(field => field.key))
    return [...existing, ...incoming.filter(field => !existingKeys.has(field.key))]
  }

  async function createHiddenField(dto: HiddenField, pageId: string): Promise<void> {
    if (!config.value) return

    const result = await axiosClient.request({
      url: `/sub-app/${config.value?.subAppId}/flow-configuration/${config.value.id}/flow-page/${pageId}/hidden-field`,
      method: 'POST',
      data: dto,
    })

    const page = config.value.pages.find(page => page.id === pageId)

    if (page) {
      page.hiddenFields.push({ ...result.data, pageId })
    }
  }

  async function updateHiddenField(dto: HiddenField, pageId: string): Promise<void> {
    if (!config.value) return

    const result = await axiosClient.request({
      url: `/sub-app/${config.value.subAppId}/flow-configuration/${config.value.id}/flow-page/${pageId}/hidden-field/${dto.id}`,
      method: 'PATCH',
      data: { ...dto, pageId },
    })

    const page = config.value.pages.find(page => page.id === pageId)

    if (page) {
      const existingField = page.hiddenFields.find(field => field.id === dto.id)

      if (existingField) {
        Object.assign(existingField, { ...result.data, pageId })
      }
    }
  }

  async function deleteHiddenField(id: string): Promise<void> {
    if (!config.value) return

    await axiosClient.request({
      url: `/sub-app/${config.value.subAppId}/flow-configuration/${config.value.id}/flow-page/${config.value.pages[currentPage.value]?.id}/hidden-field/${id}`,
      method: 'DELETE',
    })

    const page = config.value.pages[currentPage.value]

    if (page) {
      page.hiddenFields = page.hiddenFields.filter((field: HiddenField) => field.id !== id)
    }
  }

  async function insertPage(): Promise<void> {
    if (!config.value) return

    const { subAppId, id, type } = config.value

    const newCurrentPage = currentPage.value + 1

    const response = await axiosClient.request({
      url: `/sub-app/${subAppId}/flow-configuration/${id}/flow-page`,
      method: 'POST',
      data: { name: `${type} flow page`, order: newCurrentPage, showSubmit: true, showName: true },
    })

    config.value.pages.splice(newCurrentPage, 0, response.data)

    await syncPageOrder()

    await getFlowConfig(subAppId, type)

    currentPage.value = newCurrentPage
  }

  async function removePage(): Promise<void> {
    if (!config.value || config.value.pages.length <= 1) return

    const { subAppId, id, type } = config.value
    const page = config.value.pages[currentPage.value]

    if (!page) return

    await deleteFlowPageFields(page) // clears the page's fields first

    await axiosClient.request({
      url: `/sub-app/${subAppId}/flow-configuration/${id}/flow-page/${page.id}`,
      method: 'DELETE',
    })

    config.value.pages.splice(currentPage.value, 1)

    await syncPageOrder()

    await getFlowConfig(subAppId, type)

    if (currentPage.value >= (config.value?.pages.length ?? 1)) {
      currentPage.value = (config.value?.pages.length ?? 1) - 1
    }
  }

  async function searchDevices(email: string) {
    const response = await axiosClient.get<Array<{ deviceIds: string[] }>>(
      `/app-users?primaryEmail=${email}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
        },
      }
    )

    return response.data.flatMap(data => data.deviceIds)
  }

  async function searchAppUsers(email: string) {
    const response = await axiosClient.get<Array<{ id: string }>>(
      `/app-users?primaryEmail=${email}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
        },
      }
    )

    return response.data.map(data => data.id)
  }

  function setMimicDeviceId(id: string) {
    localStorage.setItem(DEVICE_ID, id)
    mimicDeviceId.value = id
  }

  function setMimicUserId(id: string) {
    localStorage.setItem(USER_ID, id)
    mimicUserId.value = id
  }

  function resetMimicDeviceId() {
    localStorage.removeItem(DEVICE_ID)
    mimicDeviceId.value = null
  }

  function resetMimicUserId() {
    localStorage.removeItem(USER_ID)
    mimicUserId.value = null
  }

  function snapshotFlowColours() {
    savedFlowColours.value = Object.fromEntries(
      flows.value.map(flow => [
        flow.id,
        Object.fromEntries(FLOW_COLOUR_KEYS.map(key => [key, flow[key]])) as Pick<
          FlowConfig,
          FlowColourKey
        >,
      ])
    )
  }

  function resetFlowColours() {
    for (const flow of flows.value) {
      const saved = savedFlowColours.value[flow.id]
      if (saved) Object.assign(flow, saved)
    }
  }

  function hasColourChanges(flow: FlowConfig) {
    const saved = savedFlowColours.value[flow.id]
    return !saved || FLOW_COLOUR_KEYS.some(key => flow[key] !== saved[key])
  }

  const hasFlowColourChanges = computed(() => flows.value.some(hasColourChanges))

  async function saveChangedFlowColours() {
    const changedFlows = flows.value.filter(hasColourChanges)

    await Promise.all(changedFlows.map(updateFlow))

    snapshotFlowColours()
  }

  return {
    insertPage,
    removePage,
    config,
    currentPage,
    fetchFlows,
    flows,
    updateFlow,
    createFlowField,
    deleteField,
    getTotalFlow,
    copyFlowToSubApp,
    getFlowConfig,
    saveFlow,
    saveFlowPage,
    updateFlowPage,
    saveField,
    updateField,
    syncAllFieldOrders,
    createHiddenField,
    updateHiddenField,
    deleteHiddenField,
    selectFlow,
    createFlow,
    importFlow,
    deleteFlow,
    searchDevices,
    searchAppUsers,
    mimicDeviceId,
    mimicUserId,
    setMimicDeviceId,
    setMimicUserId,
    resetMimicDeviceId,
    resetMimicUserId,
    resetFlowColours,
    snapshotFlowColours,
    hasFlowColourChanges,
    saveChangedFlowColours,
  }
})
